import React, { useState } from "react";
import {
  Box,
  Typography,
  Button,
  Fab,
  useMediaQuery,
  useTheme,
  Container,
  CircularProgress,
  Skeleton,
} from "@mui/material";
import CardGiftcardIcon from "@mui/icons-material/CardGiftcard";
import CameraAltIcon from "@mui/icons-material/CameraAlt";
import { useNavigate, useLocation } from "react-router-dom";
import { useEventData } from "../contexts/EventDataContext";
import WeddingImage from "../statics/wedding.svg"; // Replace with your image path
import { getAccessibleTextColor } from "../utils/colors"; // Assuming you have a utility function for color contrast
import { getIcons } from "../utils/icon"; // Import your icon utility function
import Loading from "../components/Loading";
import CollectionsIcon from '@mui/icons-material/Collections';

const IMAGE_URL =
  WeddingImage || "https://i.postimg.cc/rpR6WDQF/pexels-asadphoto-1024993.jpg";

const RomanticPage = () => {
  const {
    eventData,
    isLoading,
    gotoGift,
    gotoWish,
    gotoAlbum,
    gotoPreviewGift,
    gotoPreviewWish,
    gotoPreviewAlbum,
  } = useEventData();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("lg"));
  const navigate = useNavigate();
  const location = useLocation();
  const [imageLoading, setImageLoading] = useState(true);

  const mainData = eventData.main;
  const informationData = eventData.information;
  const giftData = eventData.gift;
  const themeData = eventData.theme;
  const backgroundImage = mainData.backgroundImage?.value;
  const title =
    mainData.eventTitle?.value ||
    informationData.eventName?.value ||
    "พิธีมงคลสมรส ณเดชน์ & ญาญ่า";
  const subtitle =
    mainData.eventDescription?.value ||
    informationData.description?.value ||
    "ร่วมเป็นเกียรติในพิธีมงคลสมรส ของ ณเดชน์ & ญาญ่า พร้อมรับประทานอาหารค่ำและชมการแสดงสุดพิเศษ";
  const textAlign = mainData.textPosition?.value || "center";
  const backgroundColor = themeData.backgroundColor?.value || "#FFF7DC";
  const primaryColor = themeData.mainColor?.value || "#8B4513";
  const secondaryColor = themeData.secondaryColor?.value || "#FFD700";
  const fontColor = themeData.fontColor?.value || "#7A3E00";
  const enableWish = mainData.wishButton?.enabled?.value || false;
  const iconWish = mainData.wishButton?.icon?.value || "heart";
  const textInWish = mainData.wishButton?.text?.value || "Let's Wish";
  const enableGift = giftData.giftButton?.enabled?.value || false;
  const iconGift = giftData.giftButton?.icon?.value || "gift";
  const textInGift = giftData.giftButton?.text?.value || "Send Gift";

  // ตรวจว่า path ตอนนี้เป็น preview หรือไม่
  const isPreview = location.pathname.includes("/preview");

  // เลือก handler ตามโหมด
  const handleGotoWish = () => {
    if (isPreview) {
      gotoPreviewWish();
    } else {
      gotoWish();
    }
  };

  const handleGotoGift = () => {
    if (isPreview) {
      gotoPreviewGift();
    } else {
      gotoGift();
    }
  };

  const handleGotoAlbum = () => {
    if (isPreview) {
      gotoPreviewAlbum();
    } else {
      gotoAlbum();
    }
  };

  const handleImageLoad = () => {
    setImageLoading(false);
  };

  const handleImageError = () => {
    setImageLoading(false);
  };

  if (isLoading) {
    // Show loading state while event data is being fetched
    // Show loading state while event data is being fetched
    const bgImage = eventData?.main?.backgroundImage?.value || eventData?.theme?.backgroundImage?.value;
    return <Loading message="กำลังโหลด..." backgroundImage={bgImage} />;

  }

  if (isMobile) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          background: backgroundColor,
          pb: 8,
        }}
      >
        {/* Image on top */}
        <Box sx={{ width: "100%", height: 500, position: "relative" }}>
          {imageLoading && (
            <Box
              sx={{
                position: "absolute",
                top: 0,
                left: 0,
                width: "100%",
                height: "100%",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                bgcolor: "#f5f5f5",
                zIndex: 1,
              }}
            >
              <CircularProgress sx={{ color: primaryColor }} />
            </Box>
          )}
          <img
            src={backgroundImage || IMAGE_URL}
            alt="wedding"
            onLoad={handleImageLoad}
            onError={handleImageError}
            style={{
              width: "100%",
              height: "100%",
              objectFit: "cover",
              objectPosition: "center",
              display: imageLoading ? "none" : "block",
              opacity: imageLoading ? 0 : 1,
              transition: "opacity 0.3s ease-in-out",
            }}
          />
          <Box
            sx={{
              position: "absolute",
              left: 0,
              bottom: 0,
              width: "100%",
              height: 70, // ปรับความสูงตามต้องการ
              zIndex: 2,
              lineHeight: 0,
              pointerEvents: "none",
            }}
          >
            <svg
              viewBox="0 0 500 70"
              width="100%"
              height="100%"
              preserveAspectRatio="none"
            >
              <path
                d="M0,0 Q250,70 500,0 L500,70 L0,70 Z"
                fill="url(#gradient)" // ใช้ gradient แทนสีพื้น
              />
              <defs>
                <linearGradient
                  id="gradient"
                  x1="0%"
                  y1="0%"
                  x2="100%"
                  y2="100%"
                >
                  <stop offset="0%" stopColor={backgroundColor} />
                  <stop offset="50%" stopColor={backgroundColor} />
                  <stop offset="100%" stopColor={backgroundColor} />
                </linearGradient>
              </defs>
            </svg>
          </Box>
        </Box>
        {/* Content below */}
        <Container maxWidth="sm" sx={{ pt: 4, textAlign: textAlign }}>
          <Typography
            variant="h5"
            sx={{
              color: fontColor,
              fontWeight: 700,
              mb: 2,
              fontFamily: '"Noto Sans Thai", sans-serif',
            }}
          >
            {title}
          </Typography>
          <Typography
            sx={{
              color: fontColor,
              mb: 4,
              fontSize: "1rem",
              lineHeight: 1.7,
              fontFamily: '"Noto Sans Thai", sans-serif',
            }}
          >
            {subtitle}
          </Typography>
          {enableGift && (
            <Button
              variant="contained"
              startIcon={getIcons(iconGift)}
              fullWidth
              onClick={handleGotoGift}
              sx={{
                mb: 2,
                backgroundColor: primaryColor,
                color: getAccessibleTextColor(primaryColor),
                fontWeight: 600,
                px: 4,
                borderRadius: 3,
              }}
            >
              {textInGift || "SEND GIFT"}
            </Button>
          )}
          {enableWish && (
            <Button
              variant="contained"
              startIcon={getIcons(iconWish)}
              fullWidth
              onClick={handleGotoWish}
              sx={{
                backgroundColor: secondaryColor,
                color: getAccessibleTextColor(secondaryColor),
                fontWeight: 600,
                px: 4,
                borderRadius: 3,
              }}
            >
              {textInWish || "LET'S WISH"}
            </Button>
          )}
        </Container>
        {/* Floating camera button */}
        <Fab
          onClick={handleGotoAlbum}
          sx={{
            position: "fixed",
            top: 24,
            right: 24,
            backgroundColor: primaryColor,
            color: "white",
            zIndex: 10,
          }}
        >
          <CollectionsIcon />
        </Fab>
      </Box>
    );
  }

  // ----- DESKTOP : รูปเต็มซ้าย กล่องชิดขวา -----
  return (
    <Box
      sx={{
        minHeight: "100vh",
        position: "relative",
        overflow: "hidden",
        background: backgroundColor, // ใช้สีพื้นหลังที่ต้องการ
      }}
    >
      {/* Image on left (fixed) */}
      <Box
        sx={{
          position: "fixed",
          left: 0,
          top: 0,
          width: "65vw",
          height: "100vh",
          overflow: "hidden",
          // borderRadius: "0 100px 100px 0", // ถ้าอยากใช้แบบกลมง่ายๆ
          clipPath: "ellipse(80% 100% at 0% 50%)", // ปรับ % ได้ตามใจ
        }}
      >
        {imageLoading && (
          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              bgcolor: "#f5f5f5",
              zIndex: 1,
              pr: "35%", // ปรับ padding ขวาเพื่อให้ circle อยู่ตรงกลางของพื้นที่ที่มองเห็น
            }}
          >
            <CircularProgress sx={{ color: primaryColor }} />
          </Box>
        )}
        <img
          src={backgroundImage || IMAGE_URL}
          alt="wedding"
          onLoad={handleImageLoad}
          onError={handleImageError}
          style={{
            width: "100%",
            height: "100%",
            objectFit: "cover",
            objectPosition: "center",
            display: imageLoading ? "none" : "block",
            opacity: imageLoading ? 0 : 1,
            transition: "opacity 0.3s ease-in-out",
          }}
        />
      </Box>

      <Box
        sx={{
          position: "relative",
          minHeight: "100vh",
          zIndex: 3,
          ml: "55vw",
          width: "45vw",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "flex-start",
          bgcolor: backgroundColor,
          px: 8,
        }}
      >
        <Container maxWidth="sm" sx={{ textAlign: textAlign }}>
          <Typography
            variant="h4"
            sx={{
              color: fontColor,
              fontWeight: 700,
              mb: 2,
              fontFamily: '"Noto Sans Thai", sans-serif',
            }}
          >
            {title}
          </Typography>
          <Typography
            sx={{
              color: fontColor,
              mb: 3,
              fontSize: "1rem",
              fontFamily: '"Noto Sans Thai", sans-serif',
            }}
          >
            {subtitle}
          </Typography>
          <Box
            sx={{
              width: "100%",
              bgcolor: backgroundColor,
              px: { xs: 0, md: 0 },
              pt: 1,
              pb: 1,
              display: "flex",
              flexDirection: "row",
              gap: 2,
            }}
          >
            {enableGift && (
              <Button
                startIcon={getIcons(iconGift)}
                onClick={handleGotoGift}
                sx={{
                  flex: 1,
                  backgroundColor: primaryColor,
                  color: getAccessibleTextColor(primaryColor),
                  fontWeight: 700,
                  py: 1.5,
                  borderRadius: 2,
                  fontSize: "1rem",
                  whiteSpace: "nowrap",
                }}
              >
                {textInGift || "SEND GIFT"}
              </Button>
            )}
            {enableWish && (
              <Button
                startIcon={getIcons(iconWish)}
                onClick={handleGotoWish}
                sx={{
                  flex: 1,
                  backgroundColor: secondaryColor,
                  color: getAccessibleTextColor(secondaryColor),
                  fontWeight: 700,
                  py: 1.5,
                  borderRadius: 2,
                  fontSize: "1rem",
                  whiteSpace: "nowrap",
                }}
              >
                {textInWish || "LET'S WISH"}
              </Button>
            )}
          </Box>
        </Container>
      </Box>

      {/* Floating camera button */}
      <Fab
        onClick={handleGotoAlbum}
        sx={{
          position: "fixed",
          bottom: 24,
          right: 24,
          backgroundColor: primaryColor,
          color: "white",
          zIndex: 10,
        }}
      >
        <CollectionsIcon />
      </Fab>
    </Box>
  );
};

export default RomanticPage;
